import { useLayoutEffect, useRef } from 'react';
import { Animated, Easing, Platform } from 'react-native';
import { WORLD_CUP_WELCOME_STAGGER } from './WorldCupWelcomeScreen.constants';

export interface WelcomeStaggerBlock {
  opacity: Animated.Value;
  translateY: Animated.Value;
}

/** Animated style object for a stagger block — use directly on `<Animated.View style={...}>`. */
export function staggerStyle(b: WelcomeStaggerBlock) {
  return {
    opacity: b.opacity,
    transform: [{ translateY: b.translateY }],
  };
}

/**
 * When `phase === targetPhase`, runs one staggered opacity + translateY entrance
 * (native driver, fixed block count — keeps work bounded).
 */
export function useWorldCupWelcomeStaggerEntrance(
  phase: number,
  targetPhase: number,
  blockCount: number,
): WelcomeStaggerBlock[] {
  const blocksRef = useRef<WelcomeStaggerBlock[]>([]);

  if (blocksRef.current.length !== blockCount) {
    blocksRef.current = Array.from({ length: blockCount }, () => ({
      opacity: new Animated.Value(0),
      translateY: new Animated.Value(WORLD_CUP_WELCOME_STAGGER.INITIAL_TRANSLATE_Y),
    }));
  }

  useLayoutEffect(() => {
    if (phase !== targetPhase) {
      return;
    }

    const blocks = blocksRef.current;
    blocks.forEach((b) => {
      b.opacity.setValue(0);
      b.translateY.setValue(WORLD_CUP_WELCOME_STAGGER.INITIAL_TRANSLATE_Y);
    });

    /** Web: native-driver opacity/transform often never completes → blank rules/points phases. */
    const useNativeDriver = Platform.OS !== 'web';

    const anims = blocks.map((b) =>
      Animated.parallel([
        Animated.timing(b.opacity, {
          toValue: 1,
          duration: WORLD_CUP_WELCOME_STAGGER.ITEM_DURATION_MS,
          easing: Easing.out(Easing.cubic),
          useNativeDriver,
        }),
        Animated.timing(b.translateY, {
          toValue: 0,
          duration: WORLD_CUP_WELCOME_STAGGER.ITEM_DURATION_MS,
          easing: Easing.out(Easing.cubic),
          useNativeDriver,
        }),
      ]),
    );

    const sequence = Animated.stagger(WORLD_CUP_WELCOME_STAGGER.GAP_MS, anims);
    sequence.start();
    return () => {
      sequence.stop();
    };
  }, [phase, targetPhase, blockCount]);

  return blocksRef.current;
}
